// Firebase Poll System
// Handles poll creation, voting, and real-time updates

import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  writeBatch,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

// ===== POLL MANAGEMENT =====

// Create a new poll
export const createPoll = async (pollData) => {
  try {
    const poll = {
      ...pollData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true,
      totalVotes: 0
    };

    const docRef = await addDoc(collection(db, 'polls'), poll);
    return { success: true, pollId: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update poll
export const updatePoll = async (pollId, updateData) => {
  try {
    const pollRef = doc(db, 'polls', pollId);
    await updateDoc(pollRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete poll
export const deletePoll = async (pollId) => {
  try {
    // Delete poll and all associated votes
    const batch = writeBatch(db);
    
    // Delete poll
    const pollRef = doc(db, 'polls', pollId);
    batch.delete(pollRef);
    
    // Delete all votes for this poll
    const votesQuery = query(
      collection(db, 'pollVotes'),
      where('pollId', '==', pollId)
    );
    const votesSnapshot = await getDocs(votesQuery);
    votesSnapshot.forEach((voteDoc) => {
      batch.delete(voteDoc.ref);
    });
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get poll by ID
export const getPoll = async (pollId) => {
  try {
    const pollRef = doc(db, 'polls', pollId);
    const pollSnap = await getDoc(pollRef);
    
    if (pollSnap.exists()) {
      return { success: true, data: { id: pollSnap.id, ...pollSnap.data() } };
    } else {
      return { success: false, error: 'Poll not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get all polls
export const getAllPolls = async () => {
  try {
    const pollsQuery = query(
      collection(db, 'polls'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(pollsQuery);
    
    const polls = [];
    snapshot.forEach((doc) => {
      polls.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: polls };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== REAL-TIME POLL LISTENERS =====

// Listen to poll changes
export const listenToPoll = (pollId, callback) => {
  const pollRef = doc(db, 'polls', pollId);
  
  const unsubscribe = onSnapshot(pollRef, (doc) => {
    if (doc.exists()) {
      callback({ success: true, data: { id: doc.id, ...doc.data() } });
    } else {
      callback({ success: false, error: 'Poll not found' });
    }
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// Listen to all polls
export const listenToAllPolls = (callback) => {
  const pollsQuery = query(
    collection(db, 'polls'),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(pollsQuery, (snapshot) => {
    const polls = [];
    snapshot.forEach((doc) => {
      polls.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: polls });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// ===== VOTING SYSTEM =====

// Vote on a poll
export const voteOnPoll = async (pollId, userId, optionId) => {
  try {
    // Use transaction to ensure data consistency
    const result = await runTransaction(db, async (transaction) => {
      // Check if user has already voted
      const existingVoteQuery = query(
        collection(db, 'pollVotes'),
        where('pollId', '==', pollId),
        where('userId', '==', userId)
      );
      const existingVoteSnapshot = await getDocs(existingVoteQuery);
      
      if (!existingVoteSnapshot.empty) {
        throw new Error('User has already voted on this poll');
      }

      // Check if poll is still active
      const pollRef = doc(db, 'polls', pollId);
      const pollSnap = await transaction.get(pollRef);
      
      if (!pollSnap.exists()) {
        throw new Error('Poll not found');
      }
      
      const pollData = pollSnap.data();
      if (!pollData.isActive) {
        throw new Error('Poll is not active');
      }
      
      if (pollData.endTime && new Date(pollData.endTime) <= new Date()) {
        throw new Error('Poll has expired');
      }

      // Add vote
      const voteRef = doc(collection(db, 'pollVotes'));
      transaction.set(voteRef, {
        pollId,
        userId,
        optionId,
        votedAt: serverTimestamp()
      });

      // Update poll vote count
      transaction.update(pollRef, {
        totalVotes: pollData.totalVotes + 1,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get votes for a poll
export const getPollVotes = async (pollId) => {
  try {
    const votesQuery = query(
      collection(db, 'pollVotes'),
      where('pollId', '==', pollId)
    );
    const snapshot = await getDocs(votesQuery);
    
    const votes = [];
    snapshot.forEach((doc) => {
      votes.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: votes };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Listen to poll votes in real-time
export const listenToPollVotes = (pollId, callback) => {
  const votesQuery = query(
    collection(db, 'pollVotes'),
    where('pollId', '==', pollId)
  );

  const unsubscribe = onSnapshot(votesQuery, (snapshot) => {
    const votes = [];
    snapshot.forEach((doc) => {
      votes.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: { votes } });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// ===== POLL ANALYTICS =====

// Get poll results
export const getPollResults = async (pollId) => {
  try {
    const votesQuery = query(
      collection(db, 'pollVotes'),
      where('pollId', '==', pollId)
    );
    const votesSnapshot = await getDocs(votesQuery);
    
    const votes = [];
    votesSnapshot.forEach((doc) => {
      votes.push({ id: doc.id, ...doc.data() });
    });

    // Get poll data
    const pollRef = doc(db, 'polls', pollId);
    const pollSnap = await getDoc(pollRef);
    
    if (!pollSnap.exists()) {
      return { success: false, error: 'Poll not found' };
    }

    const pollData = pollSnap.data();
    
    // Calculate results
    const results = {};
    pollData.options.forEach(option => {
      results[option.id] = {
        optionId: option.id,
        text: option.text,
        votes: votes.filter(vote => vote.optionId === option.id).length,
        percentage: 0
      };
    });

    const totalVotes = votes.length;
    Object.keys(results).forEach(optionId => {
      if (totalVotes > 0) {
        results[optionId].percentage = Math.round((results[optionId].votes / totalVotes) * 100);
      }
    });

    return { 
      success: true, 
      data: {
        poll: { id: pollId, ...pollData },
        results,
        totalVotes
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== ADMIN FUNCTIONS =====

// Close poll (admin only)
export const closePoll = async (pollId) => {
  try {
    const pollRef = doc(db, 'polls', pollId);
    await updateDoc(pollRef, {
      isActive: false,
      closedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Extend poll duration (admin only)
export const extendPollDuration = async (pollId, newEndTime) => {
  try {
    const pollRef = doc(db, 'polls', pollId);
    await updateDoc(pollRef, {
      endTime: newEndTime,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== UTILITY FUNCTIONS =====

// Check if poll is active
export const isPollActive = (poll) => {
  if (!poll.isActive) return false;
  if (poll.endTime && new Date(poll.endTime) <= new Date()) return false;
  return true;
};

// Get time remaining for poll
export const getTimeRemaining = (endTime) => {
  if (!endTime) return null;
  const now = new Date().getTime();
  const end = new Date(endTime).getTime();
  return Math.max(0, end - now);
};

export default {
  // Poll management
  createPoll,
  updatePoll,
  deletePoll,
  getPoll,
  getAllPolls,
  
  // Real-time listeners
  listenToPoll,
  listenToAllPolls,
  
  // Voting
  voteOnPoll,
  getPollVotes,
  listenToPollVotes,
  
  // Analytics
  getPollResults,
  
  // Admin functions
  closePoll,
  extendPollDuration,
  
  // Utilities
  isPollActive,
  getTimeRemaining
};
